import type { Locale } from '@mentimeter/i18n-core';
import type { Series as LegacySeries } from '@mentimeter/legacy-series-types/series';
import type {
  QuizPlayerPostData,
  QuizPlayerScore,
  QuizResult,
} from '@mentimeter/quiz-types';
import type { DeepPartial } from '@mentimeter/ts-utils';
import type Ably from 'ably';
import type {
  AxiosError,
  AxiosPromise,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

export type Regions = 'eu' | 'us';

export function isRegion(region: string | undefined): region is Regions {
  return region === 'eu' || region === 'us';
}

export interface ServiceUrls {
  core: string;
  quiz: string;
  blab: string;
}

/**
 * Response interface received when requesting what region a user is tied to.
 */
export interface RegionResponse {
  /**
   * The region tied to the email sent along with the request body.
   */
  region: Regions;
  /**
   * @param exists - determines if a user exists with the given email or not.
   */
  exists: boolean;
  /**
   * Base url for the server of the returned region
   */
  service_urls: ServiceUrls;
}

export interface ImportPreflightResponse {
  url: string;
  fields: Record<string, string>;
  host_url: string;
}

export interface ImportPostResponse {
  id: string;
}

export interface ConversionInfoT {
  id: string;
  input: { ext: any };
  output: { files: string[]; dir: string; filename: string };
}

export interface MediaParams {
  natural_height: number;
  natural_width: number;
  height: number;
  width: number;
  x: number;
  y: number;
}

export interface ImageResourcePreset {
  url: string;
  width?: number | undefined;
  height?: number | undefined;
}

type ImagePresetKeys =
  | 'source'
  | 'original'
  | 'preview'
  | 'small'
  | 'medium'
  | 'large'
  | 'huge';

type ImageResourcePresets = Record<ImagePresetKeys, ImageResourcePreset>;

export interface ImageResource {
  _object: string;
  _url: string;
  id: string;
  url: string;
  alt_description: string | null;
  presets: ImageResourcePresets;
  crop: MediaParams | null;
  original_height?: number;
  original_width?: number;
  s3_key?: string;
}

/*
 * Reaction
 */
export type Reaction =
  | 'heart1'
  | 'cat'
  | 'thumbsup'
  | 'thumbsdown'
  | 'question';

export type CampaignResponseT = ApiObjectT<{
  id: string;
  name: string;
  plan: string;
  data: { licenses: number; days: number };
}>;

export type CampaignErrorT = AxiosError<{
  code: string;
  data: Record<string, unknown>;
  message: string;
  status: number;
}>;

export type FolderId = number | null;

export interface ModuleT {
  id: ModuleId;
  name: string;
  type: QuestionType;
  description: {
    full: string;
    short: string;
  };
  developer?: {
    name: string;
    urls: {
      website: string;
      terms_of_use: string;
      privacy: string;
    };
  };
  assets?: {
    icon?: string;
    preview?: string;
    screenshots?: Array<string>;
  };
}

/*
 * Theme Types
 */
export type ThemeColor = string;

export interface ThemeFont {
  name: string;
  key: string;
  body: string;
  heading?: string;
}

export type ConfigIdT =
  | 'particle-blue'
  | 'explore'
  | 'waves'
  | 'colorjoy'
  | 'greenhouse'
  | 'space'
  | 'nature'
  | 'gradient'
  | 'waves-blue'
  | 'waves-purple'
  | 'business-light'
  | 'business-dark';

export interface Theme {
  logo?: ImageResource;
  logo_url: string;
  logo_cropped_image_url?: string | undefined;
  background_url: string;
  background_cropped_image_url?: string | undefined;
  background_escaped_path?: string;
  logo_escaped_path?: string;
  background_media_params?: MediaParams;
  logo_media_params?: MediaParams;
  bar_color: Array<ThemeColor>;
  bar_text_color: Array<ThemeColor>;
  background_color: ThemeColor;
  text_color: ThemeColor;
  font: ThemeFont;
  backdrop_alpha: number;
  config_id: ConfigIdT;
  is_public: boolean;
}

/*
 * Question
 */
export type QuestionType =
  | 'choices'
  | 'choices_images'
  | 'free-text'
  | 'open'
  | 'slide'
  | 'pin_on_image'
  | 'prioritisation'
  | 'qfa'
  | 'quiz'
  | 'quiz_open'
  | 'quiz_leaderboard'
  | 'ranking'
  | 'rating'
  | 'scales'
  | 'wordcloud'
  | 'metadata'
  | 'instructions'
  | 'miro';

export type QuestionSubType =
  | null
  | 'bullets'
  | 'cards'
  | 'choices'
  | 'cloud'
  | 'donut'
  | 'dots'
  | 'flow'
  | 'four-values'
  | 'horizontal'
  | 'onebyone'
  | 'open'
  | 'pie'
  | 'scales'
  | 'image'
  | 'spider'
  | 'two-values';

export interface QuestionChoiceImage {
  image?: ImageResource | null;
  cropped_image_url?: string | undefined;
  image_path?: string | undefined;
  image_url?: string | undefined;
  image_alt_description?: string;
  image_escaped_path?: string | undefined;
  media_params?: MediaParams | undefined;
}

export interface LanguageT {
  common: Record<string, string>;
  content: Record<string, string>;
  presentation: Record<string, string>;
  voting: Record<string, string>;
  controls: Record<string, string>;
}

export enum QuestionModuleCode {
  Presentation = 'presentation',
  Content = 'content',
  Voting = 'voting',
  Controls = 'controls',
}

export interface BadRequest {
  message: string;
  status: number;
  code: string;
  extra: unknown;
}

export interface Unauthorized {
  message: string;
  status: number;
  code: string;
  extra: unknown;
}

export interface AxiosErrorToJSON {
  config: Pick<
    InternalAxiosRequestConfig,
    | 'adapter'
    | 'baseURL'
    | 'data'
    | 'headers'
    | 'maxBodyLength'
    | 'maxContentLength'
    | 'method'
    | 'timeout'
    | 'transformRequest'
    | 'transformResponse'
    | 'transitional'
    | 'url'
    | 'validateStatus'
    | 'xsrfCookieName'
    | 'xsrfHeaderName'
  >;
  message: string;
  name: string;
  stack: string;
  status: number;
  response?: AxiosResponse;
}

export interface FontT {
  body: string;
  key: string;
  name: string;
}

/*
 * Series
 */
export type PaceMode = 'audience' | 'presenter';

export interface Pace {
  state: 'idle' | 'finished';
  mode: PaceMode;
  /**
   * @deprecated use value returned from new presentation state
   */
  active: string;
  counter: number;
}

export type ParticipationIdentityMode = 'anonymous' | 'identified';
export type ParticipationIdentityVisibility = 'everyone' | 'collaborators-only';

export type ParticipationAuthenticationMode =
  | 'authenticated'
  | 'unauthenticated';

export interface TemplateData {
  published?: boolean;
  action_title?: string;
  description?: string;
  tags?: string[];
  category?: string;
  created_at?: string;
  id?: number;
  series_id?: string;
  updated_at?: string;
  questions?: Question[];
  use_for_add_slide_popover?: boolean;
}

export interface QuestionExample {
  dummy_quiz_result: QuizPlayerScore[];
  dummy_results: SessionResult;
  dummy_qfa_result: Qfa;
  id: string;
  question: Question;
  title: string;
}

export interface ThemeSettingsUpdateHashT {
  background_color: string;
  background_url?: string | null | undefined;
  background_media_params?: MediaParams | null | undefined;
  background_escaped_path?: string | null | undefined;
  background_path?: string | null | undefined;
  backdrop_alpha: number;
  bar_color: Array<string>;
  bar_text_color: Array<string>;
  is_public?: boolean | null | undefined;
  logo_url?: string | null | undefined;
  logo_media_params?: MediaParams | null | undefined;
  logo_escaped_path?: string | null | undefined;
  logo_path?: string | null | undefined;
  text_color: string;
  config_id: string;
  font?: FontT | null | undefined;
}

export interface ThemeUpdateHashT {
  id?: string | null | undefined;
  name?: string | null | undefined;
  editable?: boolean | null | undefined;
  public?: boolean | null | undefined;
  owner?: boolean | null | undefined;
  owner_name?: string | null | undefined;
  shared_with_workspace?: boolean | null | undefined;
  pro_theme?: boolean | null | undefined;
  settings?: ThemeSettingsUpdateHashT | null | undefined;
  shared_with_groups?: Array<string>;
}

export interface ThemeSettingsT {
  background_color: string;
  background_url?: string;
  background_media_params?: MediaParams | null | undefined;
  background_escaped_path?: string;
  background_cropped_image_url?: string | null;
  background_path?: string;
  backdrop_alpha: number;
  bar_color: Array<string>;
  bar_text_color: Array<string>;
  is_public?: boolean;
  logo_url?: string;
  logo_media_params?: MediaParams | null | undefined;
  logo_escaped_path?: string;
  logo_cropped_image_url?: string;
  logo_path?: string;
  text_color: string;
  config_id: string;
  font?: FontT;
}

export interface ThemeResponseT {
  id: number;
  name: string;
  unwantedProp: string;
  disabled: boolean;
  editable: boolean;
  owner: boolean;
  owner_name: string;
  pro_theme: boolean;
  public: boolean;
  requires_features: boolean;
  settings: ThemeSettingsT;
  shared_with_workspace: boolean;
  user_is_admin_of_workspace: boolean;
  shared_with_groups: Array<string>;
}

export type ThemesResponseT = Array<ThemeResponseT>;

export interface BlacklistedAnswersResponse {
  results: Array<{
    question: string;
    createdAt: number;
  }>;
}

/*
 * Questions from Audience
 */
export interface Qfa {
  sort_by_upvote: boolean;
  total_count: number;
  current_page: number;
  page_size: number;
  next_page: null | string;
  data: Array<QfaQuestion>;
}

export interface QfaQuestion {
  id: number;
  question: string;
  session: number;
  created_at: string;
  archived_at: null | string;
  accepted_at: null | string;
  rejected_at: null | string;
  bucket?: 'archived' | 'accepted' | 'rejected' | 'pending';
  upvotes: number;
  is_profanity: boolean;
}

export interface QuestionChoice extends QuestionChoiceImage {
  id: number;
  label: string;
  position: number;
  correct_answer: boolean;
  delete?: true | undefined;
}

export interface QuestionGrid {
  id: string | number;
  bright: string;
  dark: string;
  name: string;
  type: 'default' | 'custom';
  editable: boolean;
  rotation?: 0 | 90 | 180 | 270 | 360;
  url?: string;
  cropped_image_url?: string | null;
  media_params?: MediaParams;
  altText?: string;
  image_alt_description?: string;
}

export interface QuestionRange {
  min: number;
  max: number;
}

export interface QuestionDimension {
  label?: string | null;
  min: string;
  max: string;
  mid_values?: Array<string>;
}

export type QuestionFieldType =
  | 'text'
  | 'email'
  | 'checkbox'
  | 'dropdown'
  | 'date';

export interface QuestionField {
  id: number;
  label: string;
  type: QuestionFieldType;
  options: Array<string>;
  position: number;
  is_required: boolean;
  delete?: true | undefined;
}

export type QuestionSlideImageType = 'image-frame' | 'image-background';

export type QuestionResult = Record<string, any> | string | number;

export type QuestionSlideType =
  | 'big'
  | 'bullets'
  | 'document'
  | 'free-text'
  | 'google-slides'
  | 'heading'
  | 'image'
  | 'images'
  | 'instructions'
  | 'miro'
  | 'number'
  | 'paragraph'
  | 'powerpoint'
  | 'quote'
  | 'slide'
  | 'video';

export type QuestionDate = string;

export interface Layout {
  alignment_x: 0 | 1 | 2;
  alignment_y: 0 | 1 | 2;
  font_size_offset: -2 | -1 | 0 | 1 | 2;
  size: '0%' | '10%' | '25%' | '33%' | '50%' | '66%';
  type:
    | 'default'
    | 'full-bg'
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'side-by-side'
    | 'side-by-side-right';
}

export const VALID_MODULE_IDS = [
  '100-points',
  'big',
  'bullets',
  'choices',
  'deprecated',
  'document',
  'free-text',
  'google-slides',
  'heading',
  'image',
  'instructions',
  'metadata',
  'miro',
  'number',
  'numerical-question',
  'open',
  'paragraph',
  'pin-on-image',
  'powerpoint',
  'qfa',
  'quiz-choices',
  'quiz-leaderboard',
  'quiz-open',
  'quote',
  'ranking',
  'rating',
  'scales',
  'video',
  'wordcloud',
] as const;

export type ValidModuleId = (typeof VALID_MODULE_IDS)[number];

export type ModuleId = 'deprecated' | ValidModuleId;

export interface Question {
  id: string;
  series_id?: string;
  /**
   * @deprecated This is a multi-mapped property depending on slide type/module id. All `*type` attributes have been aggregated into `StaticContent.type` and/or `InteractiveContent.styling.type`
   * together with `InteractiveContent.styling.visualization` depending on if there is any interactive content or not.
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent.type} for more information.
   */
  type: QuestionType;
  slug: string;
  active: boolean;
  /**
   * @deprecated Use `Slide.title` instead.
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide}
   */
  question: string;
  /**
   * @deprecated For `free-text`, use `StaticContent.styledBody` instead. For other types on interactive slides, use `InteractiveContent.styling.styledAttributes.[property]`.
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent} or {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent} for more information.
   */
  question_styled: Record<string, unknown>;
  /**
   * @deprecated Use `InteractiveContent.choices` instead.
   *
   * -----
   * Refer to the  {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.choices} for more information.
   */
  choices: Array<QuestionChoice>;
  /**
   * @deprecated Use `InteractiveContent.choices` instead.
   *
   * -----
   * Refer to the  {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.choices} for more information.
   */
  fields: Array<QuestionField>;
  /**
   * @deprecated This is a multi-mapped property depending on slide type/module id. All `*type` attributes have been aggregated into `StaticContent.type` and/or `InteractiveContent.styling.type`
   * together with `InteractiveContent.styling.visualization` depending on if there is any interactive content or not.
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.styling.visualization} for more information.
   */
  sub_type: QuestionSubType;
  /**
   * @deprecated This is a multi-mapped property depending on slide type/module id. All `*type` attributes have been aggregated into `StaticContent.type` and/or `InteractiveContent.styling.type`
   * together with `InteractiveContent.styling.visualization` depending on if there is any interactive content or not.
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide} for more information.
   */
  slide_type: QuestionSlideType;
  updated_at: QuestionDate;
  empty_result: null;
  result: SessionResult;
  do_not_use_id: number;
  /**
   * @deprecated Use `InteractiveContent.countdown` instead.
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.countdown} for more information.
   */
  countdown_to_vote: number;
  /**
   * @deprecated Not used anymore.
   *
   * -----
   * Refer to the {@link https://tinyurl.com/menti-domain-model-2024 migration sheet} for more information.
   */
  update_vote_active: boolean;
  /**
   * @deprecated This is a multi-mapped property depending on slide type/module id. Most commonly mapped to `InteractiveContent.description` or `*StaticContent.body|subheading` but usage may vary.
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent} or {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent} for more information.
   */
  question_description: string;
  /**
   * @deprecated Use `InteractiveContent.styling.styledAttributes.[property]` instead. `property` refers to the unstyled attribute name, e.g. `title` or similar
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent} for more information.
   */
  question_description_styled: Record<string, unknown>;
  title_meta: string;
  /**
   * @deprecated Use `InteractiveContent.styling.xAxisLabels|yAxisLabels` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.styling} for more information.
   */
  dimensions: [QuestionDimension, QuestionDimension];
  /**
   * @deprecated Use `StaticContent.styling.listDisplayMode` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent.styling} for more information.
   */
  show_all_bullets: boolean;
  /**
   * @deprecated Use `InteractiveContent.voteSettings.voteOnResponsePolicy` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.voteSettings} for more information.
   */
  voting_on_answers: boolean;
  /**
   * @deprecated Use `InteractiveContent.voteSettings.maxEntriesPerResponse` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.voteSettings} for more information.
   */
  multiple_votes_on_answers: boolean;
  steps: number;
  linearNavigationSteps: Array<string>;
  /**
   * @deprecated Use `StaticContent.styling.listStyle` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent.styling} for more information.
   */
  numbered_list: boolean;
  duration_vote: number;
  duration_upvote: number;
  /**
   * @deprecated Use `interactiveContent.correctAnswerMode` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.correctArea} for more information.
   */
  show_correct: boolean;
  image_as_background: boolean;
  /**
   * @deprecated Use `InteractiveContent.styling.responseCountDisplayMode` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.styling} for more information.
   */
  percent_default: boolean;
  /**
   * @deprecated Use `InteractiveContent.maxEntries` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.maxEntries} for more information.
   */
  max_votes: number;
  /**
   * @deprecated Use `InteractiveContent.styling.gridImageKey|gridImageRotation` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.styling} for more information.
   */
  grid: QuestionGrid;
  /**
   * @deprecated Use `InteractiveContent.responseRange` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.responseRange} for more information.
   */
  range: QuestionRange;
  /**
   * @deprecated Use `InteractiveContent.styling.answersPerResponse` instead
   *
   * NOTE: Not fully supported at the time of this writing
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent} for more information.
   */
  max_nb_words: number;
  /**
   * @deprecated Use `StaticContent.imagePosition` instead
   *
   * -----
   * Refer to t{@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent} for more information.
   */
  slide_image_type: QuestionSlideImageType;
  /**
   * @deprecated Use `InteractiveContent.image.presets.original.url` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.image} for more information.
   */
  question_image_url: string | null;
  /**
   * @deprecated Use `StaticContent.url` instead (only when `StaticContent.type` is `video`)
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent} for more information.
   */
  question_video_embed_url?: string | undefined;
  /**
   * @deprecated Use `StaticContent.layout.text*` attributes instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent.layout} for more information.
   */
  layout?: Layout | undefined;
  /**
   * @deprecated Use `InteractiveContent.styling.showAverage` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.styling} for more information.
   */
  scales_total_average: boolean;
  /**
   * @deprecated Use `InteractiveContent.responsePolicy` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.responsePolicy} for more information.
   */
  hide_skip: boolean;
  reactions: Array<Reaction> | null;
  /**
   * @deprecated Use `Slide.instructionsBarVisibility` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide}} for more information.
   */
  hide_voting_instructions: boolean;
  /**
   * @deprecated Use `Slide.qrCodeVisibility` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide} for more information.
   */
  hide_join_instructions: boolean;
  hide_leaderboard: boolean;
  /**
   * @deprecated Use `Slide.responseVisibility` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide} for more information.
   */
  hide_results: boolean;
  hide_logo: boolean;
  media_params: MediaParams;
  /**
   * @deprecated Use `StaticContent.image` or `InteractiveContent.image` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent} or {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent} for more information.
   */
  image_alt_description?: string | undefined;
  segment_by: string;
  /**
   * @deprecated Use `Slide.design.colors.custom` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide.design} for more information.
   */
  override_theme: boolean;
  /**
   * @deprecated Use `Slide.design` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide.design} for more information.
   */
  theme_settings?: Theme;
  /**
   * @deprecated This is a multi-mapped property depending on slide type/module id. All `*type` attributes have been aggregated into `StaticContent.type` and/or `InteractiveContent.styling.type`
   * together with `InteractiveContent.styling.visualization` depending on if there is any interactive content or not.
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide} for more information.
   */
  module_id: ModuleId;
  /**
   * @deprecated Use `Slide.visibility` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide} for more information.
   */
  skip_slide: boolean;
  /**
   * @deprecated Use `InteractiveContent.scoring` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.scoring} for more information.
   */
  time_based_scoring: boolean;
  module_custom_data: Record<string, any> | null;
  /**
   * @deprecated Use `InteractiveContent.responsePolicy` instead. Will be either `no-restriction` or `single-response-limited-choices`
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.responsePolicy} for more information.
   */
  multiple_votes: boolean | null;
  /**
   * @deprecated Use `InteractiveContent.image` or `StaticContent.image` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent} or {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent} for more information.
   */
  image?: ImageResource | null;
  /**
   * @deprecated Use `InteractiveContent.image.presets.original.url` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent} or {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1747814709#gid=1747814709 StaticContent} for more information.
   */
  cropped_image_url?: string | null | undefined;
  /**
   * @deprecated Use `InteractiveContent.correctArea.area` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=1641506766#gid=1641506766 InteractiveContent.correctArea} for more information.
   */
  correct_area?: number[][] | undefined | null;
  /**
   * @deprecated Use `Slide.hasComments` instead
   *
   * -----
   * Refer to {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=999677221#gid=999677221 Slide} for more information.
   */
  has_comments: boolean;
}

export type PaceT = Pace;

/** @deprecated Deprecated for the same reason as {@link Series}. It should be replaced with types related to slide deck-specific endpoints. */
export interface SeriesPost {
  name?: string;
  pace?: PaceMode;
  copy?: string;
  voter_results_enabled?: boolean;
  questions?: Partial<Question>[] | undefined;
  with_folder?: boolean;
  folder_id?: FolderId | undefined;
  theme_id?: number;
  public_theme_name?: string;
  reactions?: Array<Reaction> | undefined;
  workspace_series?: boolean;
  track_context?: string;
  qfa_active?: boolean;
  comments_enabled?: boolean;
  vote_again_enabled?: boolean;
  closed_for_voting?: boolean;
  qfa_moderation_enabled?: boolean;
}

/**
 * @deprecated This type is deprecated. It is being replaced
 * with an application-specific `SeriesWithSlideDeck` type.
 */
export interface Series {
  created_at: string;
  folder_id: number | null;
  /**
   * @deprecated Use `SlideDeck.slideDeckPublicKey` instead.
   *
   * Note: Take care when using the new model IDs as some operations/functions still require public key.
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  id: string;
  /**
   * @deprecated Use.. * drum roll * `SlideDeck.name` instead.
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  name: string;
  owner_display_name: string;
  /**
   * @deprecated Use `SlideDeck.ownershipSettings.ownerId` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  owner_id: number;
  owner_name: string;
  /**
   * @deprecated Use `SlideDeck.slides` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  questions: Array<Question>;
  tags: Array<string> | null;
  template_data: TemplateData;
  updated_at: string;
  /**
   * @deprecated Use `SlideDeck.participationSettings.participationKey` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  vote_key: string;
  /**
   * @deprecated Use `SlideDeck.ownershipSettings.workspaceId ` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  workspace_id: number | null;
  /**
   * @deprecated Use `SlideDeck.participationSettings.participationExpiredResponsesDeletedAt` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  expired_responses_deleted_at: string | null;
  has_results: boolean;
  /**
   * @deprecated Use `SlideDeck.participationSettings.participationResponseMode` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  closed_for_voting: boolean;
  /**
   * @deprecated Use `SlideDeck.liveChatSettings.liveChatMode` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  comments_enabled: boolean;
  dev_user: boolean;
  disable_share_results: boolean;
  hide_leaderboard: boolean;
  /**
   * @deprecated Use `SlideDeck.presentationSettings.presentationLanguage` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  language: string;
  manual_start: boolean;
  /**
   * @deprecated Use `SlideDeck.musicSettings.url` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  music_url: string;
  /**
   * @deprecated
   *
   * `pace.mode` - Use `SlideDeck.participationSettings.participationMode` instead
   * `pace.active` - Not migrated yet
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  pace: PaceT;
  /**
   * @deprecated Use `SlideDeck.participationSettings.participationIdentityMode` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  participation_identity_mode: ParticipationIdentityMode | null;
  /**
   * @deprecated Use `SlideDeck.participationSettings.participationIdentityModePolicy` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  participation_identity_mode_policy:
    | 'enabled'
    | 'disabled_by_workspace_setting';
  /**
   * @deprecated Use `SlideDeck.participationSettings.participationIdentityVisibility` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  participation_identity_visibility: ParticipationIdentityVisibility;
  /**
   * @deprecated Use `SlideDeck.participationSettings.participationAuthenticationMode` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  participation_authentication_mode: ParticipationAuthenticationMode | null;
  /**
   * @deprecated Use `SlideDeck.participationSettings.participationAuthenticationPolicy` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  participation_authentication_policy: 'available' | 'unavailable';
  /**
   * @deprecated Use `SlideDeck.presentationTimer.presentationStartedAt` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  presentation_time: number;
  /**
   * @deprecated Use `SlideDeck.profanitySettings.profanityFilters` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  profanity_lang: string;
  public_theme_name?: string;
  /**
   * @deprecated Use `SlideDeck.qaSettings.enablementScope` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  qfa_active: boolean;
  /**
   * @deprecated Use `SlideDeck.qaSettings.questionVisibility` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  qfa_intercom_enabled: boolean;
  /**
   * @deprecated Use `SlideDeck.qaSettings.moderationPolicy` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  qfa_moderation_enabled?: boolean;
  /**
   * @deprecated Use `SlideDeck.qaSettings.moderationAccessKey` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  qfa_moderation_access_key?: string;
  results_sharing?: ResultsSharingT;
  /**
   * @deprecated Use `SlideDeck.slideDeckSegmentations` instead
   *
   * - `segmentations[n].key` - `SlideDeck.slideDeckSegmentations[n].sourceInteractiveContentId`
   * - `segmentations[n].value` - `SlideDeck.slideDeckSegmentations[n].targetInteractiveContentId`
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  segmentations: Record<string, string>;
  /**
   * @deprecated Use `SlideDeck.slideDeckPublicKey` instead.
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  series_id: string;
  /**
   * @deprecated Use `SlideDeck.legacyThemeSettings` instead.
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  theme: Theme;
  /**
   * @deprecated Use `SlideDeck.themeId` instead.
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  theme_id: number;
  /**
   * @deprecated Use `SlideDeck.participationSettings.participationPolicy` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  vote_again_enabled: boolean;
  vote_id: string | number | null;
  quiz_leaderboard: boolean;
  hide_results: boolean;
  /**
   * @deprecated Use `SlideDeck.reactionSettings.reactionsAllowed` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  reactions: Array<Reaction> | null;
  /**
   * @deprecated Use `SlideDeck.ownershipSettings.collaborationMode` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  co_edited: boolean;
  /**
   * @deprecated Use `SlideDeck.ownershipSettings.folderName` instead
   *
   * -----
   * Refer to the {@link https://docs.google.com/spreadsheets/d/13HclNq2xtJmoLT3vN4YTKDkh50dlG1ZG7w-5hmWbgyY/edit?gid=0#gid=0 migration sheet} for more information.
   */
  folder_name: string | null;
}

export interface SeriesForPresentation {
  questions: Array<
    Pick<Question, 'linearNavigationSteps'> & { public_key: string }
  >;
  slide_deck_steps?: Array<{
    slide_public_key: string;
    steps: Array<string>;
  }>;
}

/**
 * @deprecated This type is deprecated. It is being replaced
 * with `SeriesWithSlideDeck` from `@mentimeter/voting-schema`.
 */
export type VotingSeries = Omit<
  Series,
  | 'owner_display_name'
  | 'owner_name'
  | 'series_id'
  | 'participation_identity_mode_policy'
>;

export interface Session {
  id: number;
  csat_done: boolean;
  current?: boolean;
}

export interface SeriesHasResultsResponse {
  has_results: boolean;
}

export type GetSeriesByVoteId = (id: string) => AxiosPromise<Series>;

export interface TemplateResponse {
  description: string;
  shared_with_organization: boolean;
  shared_with_groups: string[];
  series: Series;
}

export interface TemplatePutResponse
  extends Omit<TemplateResponse, 'shared_with_groups'> {
  id: string;
  shared_with_groups?: { id: string }[];
}

export interface PublicTemplatePutResponse {
  id: string;
  series_id: string;
  preview_question_admin_key: string;
  published: boolean;
}

export type PublicTemplateWithSeriesT = PublicTemplateT & { series: Series };

export interface PublicTemplateT {
  id: number;
  series_id: string;
  series?: LegacySeries;
  preview_question_admin_key: string;
  preview_question_hash: string;
  published: boolean;
  category_id?: number | null;
  name: string;
  description: string;
  slide_count: number;
}

export type PostPublicTemplate = Pick<
  PublicTemplateT,
  | 'series_id'
  | 'preview_question_admin_key'
  | 'published'
  | 'category_id'
  | 'description'
>;

export interface PublicTemplatesCategoryT {
  id: number;
  main_usage: UserProfileT['main_usage'];
  name: string;
  description: string;
  slug: string;
}

export type PostPublicTemplatesCategory = Pick<
  PublicTemplatesCategoryT,
  'name' | 'description' | 'main_usage'
>;

export interface QfaDataT {
  asked_on_question_key?: string | null;
  accepted_at?: string;
  id: number;
  question?: string;
  created_at?: string;
  archived_at?: string;
  deleted_at?: string;
  rejected_at?: string;
  upvotes?: number;
  bucket: BucketsT;
  is_profanity: boolean;
  display_name: string | null;
}

export type BucketsT = 'rejected' | 'archived' | 'pending' | 'accepted';

export interface QfaGetResponse {
  sort_by_upvote: boolean;
  total_count: number;
  current_page: number;
  page_size: number;
  next_page: number;
  data: Array<QfaDataT>;
}

export type UserPresentationRole = 'Owner' | 'Collaborator';

export type ResultsSharingT = `${ResultsSharingEnum}`;

export enum ResultsSharingEnum {
  ON = 'on',
  OFF = 'off',
  OPEN = 'open',
}

export enum WorkspaceRoleEnum {
  OWNER = 'owner',
  ADMIN = 'admin',
  USER = 'user',
  MEMBER_LITE = 'member_lite',
  /** 'non-member' is not a user workspace role in the database
   *  but added here for convenience throughout frontend code */
  NON_MEMBER = 'non-member',
}

export type WorkspaceRole = WorkspaceRoleEnum | undefined | null;

export enum WorkspaceMemberStatusEnum {
  DEACTIVATED = 'suspended',
  ACTIVE = 'active',
  PENDING = 'pending',
  PAYING = 'paying',
  FREE = 'free',
}

export type GroupMemberStatus = 'active' | 'pending' | 'suspended';

export enum SignupModeEnum {
  EMAIL = 'email',
  INVITE = 'invite',
  SSO = 'sso',
  STRICT_SSO = 'strict_sso',
  SOFT_SSO = 'soft_sso',
}

export enum InvitationTypeEnum {
  INVITED = 'invited',
  CLAIMED = 'claimed',
}

export interface WorkspaceMemberT {
  id: string;
  email: string;
  name?: string;
  role?: WorkspaceRole;
  status: WorkspaceMemberStatusEnum;
  joined_at?: string;
}

export interface MemberStatsT {
  active: number;
  suspended: number;
  pending: number;
  member_lite: number;
  pending_member_lite: number;
  all: number;
}

export interface SendInvitationsResponseT {
  new_invites: string[];
  resent_invites: string[];
  existing_members: string[];
}

export type ControlledDomainStateT = 'created' | 'aged' | 'verified';

export interface ControlledDomain {
  id: string;
  name: string;
  state: ControlledDomainStateT;
  txt_record: string;
  created_at: string;
  updated_at?: string;
  number_of_users: number;
}

export type ControlledDomains = ControlledDomain[];
export type AddControlledDomainsResponseT = ApiCollectionT<ControlledDomain>;
export type GetControlledDomainsResponseT = ApiObjectT<{
  verified_domains: number;
  data: ControlledDomain[];
}>;

export type GetPublicTemplateResponseT = PublicTemplateT;

export interface GetPublicTemplatesResponseT {
  data: PublicTemplateT[];
}

export interface GetPublicTemplateCategoriesResponseT {
  data: PublicTemplatesCategoryT[];
}

export type GetVerifiedControlledDomainsT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<GetControlledDomainsResponseT>;

export type AddControlledDomainsT = (
  payload: {
    domains: string[];
  },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<AddControlledDomainsResponseT>;

export type DeleteControlledDomainT = (
  domainId: string,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<void>;

export type ResendVerificationControlledDomainT = (
  payload: {
    domain_names: string[];
  },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<void>;

export type PostWorkspaceNonMembersExportT = (
  queryString: string,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<PostWorkspaceNonMembersExportResponseT>;

export type GetWorkspaceNonMembersExportT = (
  exportId: string,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<GetWorkspaceNonMembersExportResponseT>;

export type PutAnyoneCanInvite = (
  payload: { anyoneCanInvite: boolean },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<void>;

export type InviteContinue = 'editor' | 'feedback' | 'review' | undefined;
export type InviteLanding = 'teaminvite' | undefined;

export type RequestUpgradeT = (
  message: string,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise;

export enum WorkspaceInsightsXIntervalEnum {
  ALL_TIME = 'all-time',
  SIX_MONTHS = '6-months',
  THIRTY_DAYS = '30-days',
}

/**
 * Standard shape of all objects returned from the API.
 */
type ApiObjectT<Entity> = Entity & {
  resource: string;
  url: string;
};

/**
 * Standard shape of all collections returned from the API.
 */
type ApiCollectionT<Entity> = ApiObjectT<{
  data: Array<ApiObjectT<Entity>>;
}>;

/*****************************************/
/** Endpoint specific response types 👇 **/

/*****************************************/
export interface GroupT {
  id: string;
  name: string;
  description: string | null;
  creator?: string | undefined;
  memberCount: number;
  themeCount?: number;
  templateCount?: number;
}

export type GroupsT = GroupT[];

export interface GroupMemberT {
  id: string | number;
  name?: string;
  email: string;
  status: string;
  profile_picture_url: string | null;
}

export interface MembershipRoleT {
  name: WorkspaceRole;
  displayName: string;
  description: string;
  isDefault: boolean;
  dropdownName: string;
}

export type MembershipRolesT = MembershipRoleT[];

export type MemberT = ApiObjectT<{
  id: string;
  name: string | null;
  email: string;
  user_name: string;
  role: WorkspaceRole;
  access_level: number;
  status: WorkspaceMemberStatusEnum;
  presentations_count: number | null;
  last_active: string | null;
  joined_at: string | null;
  updated_at: string | null;
  profile_picture_url: string | null;
  groups: {
    memberships: { name: string }[];
  };
}>;

export type WorkspaceMembersResponseT = ApiObjectT<{
  data: MemberT[];
}>;

type GetWorkspaceNonMembersExportResponseT = ApiObjectT<{
  file_url: string;
}>;

type PostWorkspaceNonMembersExportResponseT = ApiObjectT<{
  id: string;
}>;

export interface GroupResponseT {
  id: string;
  name: string;
  description: string | null;
  creator?: string;
  member_count: number;
  theme_count: number;
  template_count: number;
}

type FetchGroupsResponseT = ApiCollectionT<GroupResponseT>;

type FetchGroupDetailsResponseT = ApiObjectT<GroupResponseT>;

type FetchGroupMembersResponseT = ApiCollectionT<{
  user: {
    id: string;
    name: string;
    email: string;
    profile_picture_url: string;
    status: GroupMemberStatus;
  };
}>;

export type {
  FetchGroupDetailsResponseT,
  FetchGroupMembersResponseT,
  FetchGroupsResponseT,
};

type GetAllT = (
  args: { isAdmin: boolean },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<FetchGroupsResponseT>;

type GetGroupT = (
  args: { id: string },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<FetchGroupDetailsResponseT>;

type CreateGroupT = (
  args: { name: string; description?: string },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<FetchGroupDetailsResponseT>;

export type UpdateGroupT = (
  args: Partial<GroupResponseT>,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<UpdateGroupT>;

type DeleteGroupT = (
  args: { id: string },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<null>;

type GetGroupMembersT = (
  args: { id: string },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<FetchGroupMembersResponseT>;

type RemoveGroupMembersT = (payload: {
  id: string;
  members: Array<{ id: string | number; status: string }>;
}) => AxiosPromise<null>;

type AddMembersT = (args: {
  id: string;
  members: Array<{ id: string; status: WorkspaceMemberStatusEnum }>;
}) => AxiosPromise<null>;

interface ApiTokenT {
  created_at: string;
  id: number;
  token: string;
}

type GetApiTokensResponseT = ApiTokenT[];

type GenerateApiTokensResponseT = ApiTokenT;

type GetApiTokensT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<GetApiTokensResponseT>;

type GenerateApiTokenT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<GenerateApiTokensResponseT>;

type RevokeApiTokenT = (
  token: string,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<null>;

export type ReactionResult = { [key in Reaction]?: number };

export interface ReactionsResults {
  respondents: number;
  results: ReactionResult;
}

export interface WorkspaceInviteRequestResponseT {
  id: string;
  user_id: number;
  workspace_id: number;
  expires_at: string;
}

export interface SegmentT {
  id: number | null;
  label: string;
  position: number;
}

export interface QuestionSession {
  respondents: number;
  results: Array<QuestionResult> | ReactionResult;
}

export type QuestionSessionResponse = Record<string, QuestionSession>;

type QuickFormResult = number;

export interface SessionResult {
  type: QuestionType;
  timestamp: number;
  results: Array<QuestionResult> | ReactionResult | QuickFormResult | string;
  respondents: number;
  segments?: Array<SegmentT>;
  votes?: any;
}

export interface QuestionResultParams {
  segment_by?: string;
  session?: number;
  isPolling?: boolean;
  isPublic?: boolean | null;
  isScreenshot?: boolean;
  transportDown?: boolean;
  countdown?: number;
}

export interface SeriesHasResultsParams {
  questionIds?: string[] | undefined;
  minRespondents?: number | undefined;
}

export type FetchUserT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<UserResponseT>;

export type GetNotificationT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<NotificationResponseT>;

export type GetUserProfileT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<UserProfileT>;

export type UpdateUserProfileT = (
  payload: Partial<Omit<UserProfileT, 'id'>>,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<UserProfileT>;

export type RevokeSessionsT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<undefined>;

export interface Config {
  baseURL: string | null | undefined;
}

export type SaveUtmFlagsT = (
  payload: {
    flags: Partial<UserFlagsT>;
  },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<UserResponseT>;

export type UpdateFlagsT = (
  flags: UserFlagsT,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<UserResponseT>;

export type UpdateSettingsT = (
  settings: Partial<UserSettingsT>,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<UserResponseT>;

export interface NotificationResponseT {
  type: 'announcement' | 'success' | 'warning';
  content: {
    message: string;
    title: string;
    meta?: {
      is_session_notification?: boolean;
      group_id?: string;
    };
    id?: string;
  };
}

type UserProfileProperty<T> = T | 'skipped' | undefined;

export interface UserProfileT {
  main_usage: Exclude<MainUsageT, LegacyMainUsageT> | null;
  use_cases: string[];
  user_id: number;
  organization_type: UserProfileProperty<InitialMainUsageId>;
  organization_size: UserProfileProperty<OrganizationSize>;
  department: UserProfileProperty<IMUDepartmentId>;
  work_role: UserProfileProperty<CompanyRoles | EducationRoles>;
  user_initial_goal: UserProfileProperty<UserInitialGoal>;
}

export const ORG_TYPE_LIST = [
  'business',
  'education',
  'public-sector',
  'non-profit',
  'student',
  'other',
] as const;

export type InitialMainUsageId = (typeof ORG_TYPE_LIST)[number];

type LegacyBusiness = 'work';

export type LegacyMainUsageT =
  | LegacyBusiness
  | 'workshops'
  | 'events'
  | 'training_sessions'
  | 'meetings'
  | 'conferences'
  | 'skipped';

export type MainUsageT =
  | 'business'
  | 'education'
  | 'other'
  | 'popular-features'
  | LegacyMainUsageT;

export const IMU_COMPANY_ROLE_LIST = [
  'freelancer',
  'team-member',
  'team-manager',
  'director',
  'executive-assistant',
  'executive',
  'other',
] as const;

export type CompanyRoles = (typeof IMU_COMPANY_ROLE_LIST)[number];

export const IMU_EDUCATION_ROLE_LIST = [
  'tl-library-staff',
  'dean-head-of',
  'director',
  'provost',
  'president-chancellor',
  'other',
] as const;

export type EducationRoles = (typeof IMU_EDUCATION_ROLE_LIST)[number];

export const ORG_SIZE_LIST = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  'Over 10001',
] as const;

export type OrganizationSize = (typeof ORG_SIZE_LIST)[number];

export const IMU_BUSINESS_DEPARTMENTS_LIST = [
  'consulting-services',
  'executive-leadership-strategy',
  'finance-legal',
  'human-resources',
  'it-infrastructure',
  'internal-communications',
  'learning-and-development',
  'marketing',
  'operations-logistics',
  'research-product-development',
  'sales',
  'business-other',
] as const;

export type IMUBusinessDepartments =
  (typeof IMU_BUSINESS_DEPARTMENTS_LIST)[number];

const imuEducationDepartments = [
  'k12-teacher-or-administrator',
  'educator-higher-education',
  'administration-and-support-higher-education',
  'education-other',
];

type IMUEducationDepartments = (typeof imuEducationDepartments)[number];

const imuPublicSectorDepartments = [
  'executive-leadership-strategy',
  'finance-legal',
  'human-resources',
  'it-infrastructure',
  'operations-logistics',
  'research-product-development',
  'policy-program-public-affairs',
  'public-sector-other',
];

type IMUPublicSectorDepartments = (typeof imuPublicSectorDepartments)[number];

const imuNonProfitDepartments = [
  'communications-advocacy',
  'executive-leadership-strategy',
  'finance-legal',
  'fundraising-development',
  'human-resources',
  'it-infrastructure',
  'operations-logistics',
  'program-management',
  'non-profit-other',
] as const;

type IMUNonProfitDepartments = (typeof imuNonProfitDepartments)[number];

export type IMUDepartmentId =
  | IMUBusinessDepartments
  | IMUEducationDepartments
  | IMUPublicSectorDepartments
  | IMUNonProfitDepartments;

export const USER_INITIAL_GOAL = [
  'learning',
  'leadership-communication',
  'collaborative-meetings',
  'feedback',
  'not-sure',
] as const;

export type UserInitialGoal = (typeof USER_INITIAL_GOAL)[number];

export type UseCaseT =
  | 'engagement'
  | 'training-educating'
  | 'insights-feedback'
  | 'not-sure'
  | 'skipped';

export interface UserFlagsT {
  opt_out_csat?: boolean;
  onboarding?: MainUsageT;
  onboarding_use_cases?: Array<UseCaseT>;
  utm_first?:
    | {
        [key: string]: string;
      }
    | undefined;
  utm_last?:
    | {
        [key: string]: string;
      }
    | undefined;
  receive_results_email?: boolean;
  ai_features_enabled?: boolean;
  mentimote_intro?: boolean;
  role_changed?: boolean;
  previous_role?: WorkspaceRole;
}

type ExternalIdentityT = 'google' | 'facebook';

export enum UserFeaturesTypeEnum {
  REGISTERED = 'registered',
  BASIC = 'basic',
  PRO = 'pro',
  ENTERPRISE = 'enterprise',
  ENTERPRISE_TRIAL = 'enterprise_trial',
}

export enum PlanKeyEnum {
  INVOICE = 'invoice',
  ENTERPRISE = 'enterprise_2021_06',
  ENTERPRISE_TRIAL = 'trial_enterprise_2020_11',
}

export enum PlanFeaturesEnum {
  PRO = 'pro',
  ENTERPRISE = 'enterprise',
}

export type SubscriptionPlanFeaturesT =
  | 'registered'
  | 'basic'
  | 'pro'
  | 'enterprise'
  | 'enterprise_trial';

export type UserFeaturesType = SubscriptionPlanFeaturesT;

export interface UserFeaturesT {
  template_editor?: boolean;
  type: UserFeaturesType;
  engaged_participants_limit?: number;
  manage_groups: boolean;
  quick_form: boolean;
  several_answers: boolean;
  private_results: boolean;
  moderation_enabled: boolean;
  groups: boolean;
  dev_user: boolean;
  export: boolean;
  series_in_workspace_limit: number;
  user_capturing: boolean;
  downgrade?: boolean;
  ai_enabled: boolean;
  ai_domain_enabled: boolean;
  suspend?: boolean;
}

interface UserSettingsT {
  profanity_lang: string | null;
}

interface UserResponseSubscriptionPlanT {
  category: PlanCategory;
}

interface UserResponseSubscriptionT {
  plan: UserResponseSubscriptionPlanT;
}

export interface UserResponseT {
  active_workspace_id: number;
  address?: string | null;
  beta_status: 'active' | 'standard' | 'inactive';
  created_at: string;
  collaboration_rights: 'no_one' | 'anyone' | 'workspace_members';
  email: string;
  external_identities?: ExternalIdentityT[] | null;
  features: UserFeaturesT;
  flags: UserFlagsT;
  fromCache?: true;
  has_password?: boolean;
  id: number;
  identifier?: string | null;
  intercom_hash?: string | null;
  name: string;
  otp: { otp: string; expires_in: number };
  role?: WorkspaceRole;
  saml: boolean;
  settings: null | UserSettingsT;
  session_token?: string;
  payments_provider?: 'stripe' | 'unknown';
  token: string;
  user_id: number;
  service_urls: ServiceUrls;
  language: Locale | undefined;
  region: Regions;
  profile_picture: ImageResource | null;
  is_verified: boolean;
  requires_email_verification: boolean;
  subscription?: UserResponseSubscriptionT;
}

export type TransactionsResponseT = Array<{
  amount: string;
  created_at: string;
  currency: string;
  id: string;
  status: string; // transaction status
  type: string; // transaction type
  payment_method_type: 'paypal_account' | 'credit_card';
  // payment method-specific fields
  paypal_email?: string;
  creditcard?: string;
  creditcard_type?: string;
}>;

export type BillingHistoryTransactionType = 'refund' | 'invoice';

export type BillingHistoryResponseT = Array<{
  amount: number;
  currency: CurrencyT;
  finalized_at: number | null;
  due_at: number | null;
  id: string;
  invoice_url: string;
  paid_at: number | null;
  status?: string;
  type: BillingHistoryTransactionType;
}>;

export interface CurrencyT {
  decimal_mark: string;
  iso_code: string;
  subunit_to_unit: number;
  symbol: string;
  symbol_first: boolean;
  thousands_separator: string;
}

export interface StripeAddressT {
  city: string;
  country: string;
  postal_code: string;
  line1: string;
  line2?: string;
  state?: string;
}

export interface StripeInvoiceDetailsT {
  po_number?: string;
  other_info?: string;
}

export enum TAXStatusTypes {
  Verified = 'verified',
  Error = 'error',
  Pending = 'pending',
}

export interface StripeCustomerTaxIdT {
  country?: string;
  type?: string;
  value?: string;
  verification_status?: TAXStatusTypes;
}

export interface StripeCustomerResponseT {
  customer: {
    name: string;
    currency?: string;
    address: StripeAddressT;
    tax_id?: StripeCustomerTaxIdT;
    invoice_details?: StripeInvoiceDetailsT;
  };
}

export enum PlanCategory {
  FREE = 'free',
  TEAMS = 'teams',
  BASIC = 'basic',
  BASIC_MONTHLY = 'basic-monthly',
  PRO = 'pro',
  EDUCATIONAL_BASIC = 'educational-basic',
  EDUCATIONAL_BASIC_MONTHLY = 'educational-basic-monthly',
  EDUCATIONAL_PRO = 'educational-pro',
  LEGACY_PLAN = 'teams',
  STUDENT_PRO = 'student-pro',
  CONFERENCE_SMALL = 'conference-small',
  CONFERENCE_LARGE = 'conference-large',
  ENTERPRISE = 'enterprise',
  PRO_TRIAL = 'pro-trial',
  ENTERPRISE_TRIAL = 'enterprise-trial',
}

export enum PaymentMethodType {
  Card = 'card',
  PayPal = 'paypal',
  Link = 'link',
  BankAccount = 'bank_account',
  VirtualAccount = 'virtual_account',
}

interface CardPaymentResponseT {
  type: PaymentMethodType.Card;
  brand?: string;
  exp_month?: number;
  exp_year?: string | number;
  last4?: string;
  expired?: boolean;
}

interface PayPalPaymentResponseT {
  type: PaymentMethodType.PayPal;
  email: string;
}

interface LinkPaymentResponseT {
  type: PaymentMethodType.Link;
  email: string;
}

interface VirtualAccountPaymentResponseT {
  type: PaymentMethodType.VirtualAccount;
  reference: string;
  iban: string;
  bic: string;
  bank_name: string;
  account_number: string;
  routing_number: string;
  swift_code: string;
  currency: string;
}

interface BankAccountPaymentResponseT {
  type: PaymentMethodType.BankAccount;
  reference: string;
  account_name: string;
  bank: string;
  iban: string;
  swift: string;
  address: string;
}

export type PaymentMethodResponseT = {
  type: PaymentMethodType;
} & (
  | CardPaymentResponseT
  | PayPalPaymentResponseT
  | LinkPaymentResponseT
  | VirtualAccountPaymentResponseT
  | BankAccountPaymentResponseT
);

export interface SubscriptionPlanT {
  key: string;
  features: string;
  name: string;
  recurring: { years: number } | 'none';
  category: PlanCategory;
  included_licenses: number;
  multiple_licenses: number | null;
  recurring_unit: string;
}

export interface PlanPriceT {
  total: number;
  per_license: number;
  currency: string;
  tax_rate?: number;
  tax?: number;
  total_including_tax?: number;
  total_excluding_tax?: number;
  license_discount?: number;
  total_discount_amount?: number;
  total_discount_percentage?: number;
}

export enum SubscriptionStatus {
  CANCELED = 'Canceled',
  PENDING = 'Pending',
  ACTIVE = 'Active',
  PAST_DUE = 'Past Due',
  EXPIRED = 'Expired',
}

export enum StripeSubscriptionStatus {
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  UNKNOWN = 'unknown',
}

export enum StripeCollectionMethod {
  CHARGE_AUTOMATICALLY = 'charge_automatically',
  SEND_INVOICE = 'send_invoice',
}

export interface PaymentT {
  billing_day_of_month: number;
  billing_period_end_date: string;
  billing_period_start_date: string;
  current_billing_cycle: number;
  first_billing_date: string;
  next_billing_date: string;
  next_billing_period_amount: number;
  paid_through_date: string;
  price: number;
  status: SubscriptionStatus;
}

export interface SubscriptionT {
  cancel_renewal?: boolean;
  collection_method?: StripeCollectionMethod;
  created_at: string;
  expired_at: string;
  end_at?: string;
  id: number;
  is_admin_or_owner?: boolean;
  licenses: number;
  payment: PaymentT | Record<string, never>;
  plan: SubscriptionPlanT;
  renewed_at?: string;
  role: string;
  provider_status: StripeSubscriptionStatus;
  status: SubscriptionStatus;
  workspace: {
    id: number;
  };
}

export interface FoldersParams {
  parent_id?: FolderId;
  name?: string;
  workspace_id?: number | null;
}

export interface SubscriptionsResponseT {
  current_subscription?: SubscriptionT;
  queued_subscriptions?: SubscriptionT[];
}

export interface RedirectToStripeCheckoutResponseT {
  checkout_url: string;
}

export interface StripeSetupCheckoutResponseT {
  setup_url: string;
}

interface TierT {
  unit_amount: number;
  up_to: number | null;
}

export interface PricesResponseT {
  key: string;
  legacy_plan_category: PlanCategory;
  product: 'pro' | 'basic';
  recurring: RecurringT | null;
  amount: number;
  billing_scheme: 'tiered' | 'per_license';
  currency: CurrencyT;
  tiers: TierT[];
}

interface RecurringT {
  unit: 'year' | 'month';
  count: number;
}

export interface ReceiptResponseT {
  address: string;
  amount: string;
  amount_excluding_vat: string;
  currency: string;
  due_date: string;
  invoice_date: string;
  invoice_number: string;
  product_name: string;
  quantity: number;
  status: 'settled';
  type: 'sale' | 'credit';
  vat: string;
}

export type GetTransactionsT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<TransactionsResponseT>;

export interface MemberUpgradeT {
  id: string;
  status: WorkspaceMemberStatusEnum;
  role: WorkspaceRole;
}

export type generateCheckoutSessionT = (
  payload: {
    plan_category: string;
    licenses: number;
    members?: MemberUpgradeT[];
    success_url: string;
    cancel_url: string;
    trial?: boolean;
  },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<RedirectToStripeCheckoutResponseT>;

export type GetSubscriptionsT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<SubscriptionsResponseT>;

export type GetPricesT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<Array<PricesResponseT>>;

export type GetPaymentMethodsT = (
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<Array<PaymentMethodResponseT>>;

export type GetReceiptT = (
  id: string,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<ReceiptResponseT>;

export interface VoteCodeResponseT {
  id: number;
  renew_after: number;
  expires_in: number;
}

type RegionWorkspaceLookupT = (
  id: string,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<RegionResponse>;

type RegionUrlsT = (
  region: Regions,
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<ServiceUrls>;

type SamlConfirmT = (
  payload: {
    token: string;
  },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<UserResponseT>;

type PostProfilePictureT = (
  data: {
    file_id: string;
    crop: MediaParams | null;
    alt_description: string | null;
  },
  config?: InternalAxiosRequestConfig,
) => AxiosPromise<ImageResource>;

type DeleteProfilePictureT = (config?: InternalAxiosRequestConfig) => void;

type DeleteUserT = (config?: InternalAxiosRequestConfig) => void;

interface UserOtpResponse {
  otp: string;
  expires_in: number;
}

export interface PresignedPostInterface {
  url: string;
  fields: Record<string, string>;
}

export interface FilePreflightResponse {
  id: string;
  s3_key: string;
  presigned_post: PresignedPostInterface;
}

export interface SessionsPostDataT {
  question_id?: string | string[];
  context:
    | 'Edit view'
    | 'Presentation view'
    | 'Mentimote'
    | 'Homeview'
    | 'Powerpoint'
    | 'Results page';
  full_reset?: boolean;
}

interface SessionsPutDataT {
  csat_done: boolean;
}

export interface QuestionWithIndex extends Question {
  insert_at_index: number;
}

export interface EditorQuestionsPostPayload {
  insert_at_index: number;
  questions: DeepPartial<Question>[];
  region?: Regions | undefined;
  client?: string | undefined;
  device_id?: string | undefined;
  track_context?: string | undefined;
}

interface TemplatePutData {
  description: string;
  shared_with_organization: boolean;
  shared_with_groups: { id: string }[];
}

export interface DiagnosticsResponse {
  series: [{ name: 'My awesome presentation' }];
}

export interface AcademyResponse {
  redirect: string;
}

export interface ResponseText {
  id: string;
  text: string;
  upvotes: number;
  cluster_id: string;
}

export interface ResponseCluster {
  id: string;
  responses: ResponseText[];
  label: string;
  clustering_id: string;
  total_upvotes: number;
}

export interface ResponseClustering {
  id: string;
  clusters: ResponseCluster[];
  score: number;
  status: 'pending' | 'complete' | 'error';
  unclustered_responses: ResponseText[];
}

export interface AdminDevLoginResponse {
  jwt: string;
}

export interface AzureADBody {
  /**
   * Microsoft Teams auth token
   */
  token: string;
  /**
   * Tenant ID
   */
  tid: string;
}

export interface ZoomIntegrationResponse {
  id: string;
  series_id: string;
  user_id: number;
  series_name: string;
  vote_key: string;
  created_at: string;
  admin_key: string;
}

export interface GetAdminUserResponse {
  data: {
    attributes: {
      email: string;
      role: string;
      created_at: string;
      updated_at: string;
    };
    id: string;
    type: string;
  };
}

export interface AiGeneratorResponse {
  id: string;
}

export type AiJobTypes = 'generate_series' | 'generate_titles';

export interface AiCreateProcessPayload {
  job_type: AiJobTypes;
}

export interface AiCreatedProcessResponse {
  id: string;
}

export type AiRunJobPayload = object;

export interface AiRunProcessPayload {
  id: string;
  method: AiJobTypes;
  use_case: string;
  topic: string;
}

export interface ImageCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CoreClient {
  campaigns: {
    get: (
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<CampaignResponseT>;
    post: (
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<CampaignResponseT>;
  };
  convert: {
    get: (
      id: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ConversionInfoT>;
    post: (
      data: { file_url: string },
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ImportPostResponse>;
    importPreflight: (
      data: { content_type: string },
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ImportPreflightResponse>;
  };
  getSeriesByVoteId: GetSeriesByVoteId;
  files: {
    post: (
      content_type: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<FilePreflightResponse>;
  };
  integrations: {
    zoom: {
      get: (
        meetingId: string,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<ZoomIntegrationResponse>;
      put: (
        meetingId: string,
        body: { series_id: string },
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<ZoomIntegrationResponse>;
      delete: (
        meetingId: string,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<void>;
    };
    msteams: {
      connect: (
        body: AzureADBody,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<RegionResponse>;
      region: (
        body: AzureADBody,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<RegionResponse>;
      authenticate: (
        body: AzureADBody,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<UserResponseT>;
    };
  };
  images: {
    put: (
      id: string,
      data: {
        crop: MediaParams | null;
        alt_description: string | null;
      },
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ImageResource>;
  };
  themes: {
    getAll: (
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ThemesResponseT>;
    get: (
      id: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ThemeResponseT>;
    post: (
      data?: Partial<ThemeResponseT>,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ThemeResponseT>;
    put: (
      id: string,
      data: Partial<ThemeUpdateHashT>,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ThemeResponseT>;
    delete: (
      id: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ThemesResponseT>;
  };
  questions: {
    result: {
      get: (
        id: string,
        params?: QuestionResultParams,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<SessionResult>;
    };
    quizPlayers: {
      get: (
        id: string,
        clearCache: boolean,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<QuizPlayerScore[]>;
      put: (
        id: string,
        data: Partial<QuizPlayerPostData>,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<QuizPlayerScore[]>;
    };
    quizResult: {
      get: (
        id: string,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<QuizResult[]>;
    };
    sessions: {
      get: (
        id: string,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<QuestionSessionResponse>;
    };
  };
  series: {
    tryModes: {
      post: (
        seriesId: string,
        data: { active_question_index?: number },
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<any>;
    };
    realtime: (
      seriesId: string,
      accessKey?: string | null,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<Ably.TokenRequest | Ably.TokenDetails>;
    getSeriesHasResults: (
      seriesId: string,
      params?: SeriesHasResultsParams,
    ) => AxiosPromise<SeriesHasResultsResponse>;
    collaborators: {
      delete: (
        seriesId: string,
        userId?: number,
        userEmail?: string,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<void>;
    };
    voteCode: {
      get: (
        id: string,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<VoteCodeResponseT>;
      post: (
        id: string,
        data: Partial<VoteCodeResponseT>,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<VoteCodeResponseT>;
    };
    sessions: {
      get: (
        seriesId: string,
        accessKey?: string | null,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<Session[]>;
      post: (
        seriesId: string,
        data: SessionsPostDataT,
        config?: InternalAxiosRequestConfig<SessionsPostDataT>,
      ) => AxiosPromise<Session[]>;
      put: (
        seriesId: string,
        sessionId: number,
        data: SessionsPutDataT,
        config?: InternalAxiosRequestConfig<SessionsPutDataT>,
      ) => AxiosPromise<Session[]>;
      delete: (
        seriesId: string,
        sessionId: number,
        config?: InternalAxiosRequestConfig<number>,
      ) => AxiosPromise<Session[]>;
    };
    template: {
      get: (
        seriesId: string,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<TemplateResponse>;
      put: (
        seriesId: string,
        data: TemplatePutData,
        config?: InternalAxiosRequestConfig<TemplatePutData>,
      ) => AxiosPromise<TemplatePutResponse>;
    };
  };
  templates: {
    post: (
      data: {
        shared_with_organization: boolean;
        series: string;
        description: string;
      },
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<TemplatePutResponse>;
    put: (
      id: string,
      arg1: {
        shared_with_organization?: boolean;
        series?: string;
        description?: string;
      },
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<TemplatePutResponse>;
  };
  templateEditor: {
    publicTemplates: {
      post: (
        data: PostPublicTemplate,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<PublicTemplateT>;
      put: (
        id: number,
        arg1: PostPublicTemplate,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<PublicTemplateT>;
      get: (
        seriesId: string,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<GetPublicTemplatesResponseT>;
    };
  };
  publicTemplates: {
    template: {
      get: (
        id: number,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<GetPublicTemplateResponseT>;
    };
    categories: {
      post: (
        data: PostPublicTemplatesCategory,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<PublicTemplatesCategoryT>;
      put: (
        id: number,
        arg1: PostPublicTemplatesCategory,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<PublicTemplatesCategoryT>;
      get: (
        queryString?: string,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<GetPublicTemplateCategoriesResponseT>;
    };
  };
  qfa: {
    get: {
      all: (
        seriesId: string,
        page: number,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<QfaGetResponse>;
      rejected: (
        seriesId: string,
        page: number,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<QfaGetResponse>;
      archived: (
        seriesId: string,
        page: number,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<QfaGetResponse>;
      accepted: (
        seriesId: string,
        page: number,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<QfaGetResponse>;
      pending: (
        seriesId: string,
        page: number,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<QfaGetResponse>;
    };
    delete: (
      id: number,
      deviceId: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<undefined>;
    deleteAll: (
      seriesId: string,
      deviceId: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<undefined>;
    archive: (
      id: number,
      deviceId: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<undefined>;
    restore: (
      id: number,
      deviceId: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<undefined>;
    reject: (
      id: number,
      deviceId: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<undefined>;
    accept: (
      id: number,
      deviceId: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<undefined>;
  };
  users: {
    saveUtmFlags: SaveUtmFlagsT;
    updateFlags: UpdateFlagsT;
    updateSettings: UpdateSettingsT;
    fetchUser: FetchUserT;
    getUnreadNotifications: GetNotificationT;
    getUserProfile: GetUserProfileT;
    updateUserProfile: UpdateUserProfileT;
    revokeSessions: RevokeSessionsT;
    profilePicture: {
      post: PostProfilePictureT;
      delete: DeleteProfilePictureT;
    };
    delete: DeleteUserT;
  };
  userOtp: {
    post: (
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<UserOtpResponse>;
  };
  subscriptions: {
    get: GetSubscriptionsT;
  };
  prices: {
    get: GetPricesT;
  };
  payments: {
    getTransactions: GetTransactionsT;
    generateCheckoutSession: generateCheckoutSessionT;
    getPaymentMethods: GetPaymentMethodsT;
    getReceipt: GetReceiptT;
  };
  workspaces: {
    settings: {
      anyoneCanInvite: {
        put: PutAnyoneCanInvite;
      };
    };
    controlledDomains: {
      getVerified: GetVerifiedControlledDomainsT;
      add: AddControlledDomainsT;
      delete: DeleteControlledDomainT;
      resendVerification: ResendVerificationControlledDomainT;
    };
    requestUpgrade: RequestUpgradeT;
  };
  groups: {
    getAll: GetAllT;
    get: GetGroupT;
    getMembers: GetGroupMembersT;
    create: CreateGroupT;
    delete: DeleteGroupT;
    update: UpdateGroupT;
    members: { delete: RemoveGroupMembersT; add: AddMembersT };
  };
  apiTokens: {
    getAll: GetApiTokensT;
    generate: GenerateApiTokenT;
    revoke: RevokeApiTokenT;
  };
  region: {
    urls: RegionUrlsT;
    workspaces: RegionWorkspaceLookupT;
  };
  auth: {
    saml: {
      confirm: SamlConfirmT;
    };
    resetPassword: (
      email: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<undefined>;
    verifyResetPasswordCode: (
      code: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<{ email: string }>;
    changePasswordWithCode: (
      data: { email: string; password: string; code: string },
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<undefined>;
  };
  moderation: (params: { questionId: string; seriesId: string }) => {
    get: (
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<BlacklistedAnswersResponse>;
    toggle: (
      answer: string,
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<void>;
    clear: (config?: InternalAxiosRequestConfig) => AxiosPromise<void>;
  };
  diagnostics: {
    get: (
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<DiagnosticsResponse>;
    put: (
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<DiagnosticsResponse>;
  };
  academy: {
    get: (config?: InternalAxiosRequestConfig) => AxiosPromise<AcademyResponse>;
  };
  presentation: {
    reactions: {
      results: {
        get: (
          questionId: string,
          config?: InternalAxiosRequestConfig,
        ) => AxiosPromise<ReactionsResults>;
      };
    };
  };

  responseClusterings: {
    post: (
      payload: { question_admin_key: string },
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<ResponseClustering>;
    assignResponse: (
      clusterId: string,
      payload: { response_ids: string[] },
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<void>;
    clusters: {
      post: (
        clusteringId: string,
        payload: { label: string },
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<ResponseCluster>;
      put: (
        cluster: ResponseCluster,
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<ResponseCluster>;
    };
  };
  admin: {
    getAdminUser: (
      config?: InternalAxiosRequestConfig,
    ) => AxiosPromise<GetAdminUserResponse>;
    auth: {
      devLogin: (
        config?: InternalAxiosRequestConfig,
      ) => AxiosPromise<AdminDevLoginResponse>;
    };
  };
}
